import React, { Fragment, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useDropzone } from "react-dropzone";
import { FaUpload, FaVideo } from "react-icons/fa";
import { MdAddAPhoto } from "react-icons/md";
import { Stack } from "@mui/material";

const MAX_FILE_SIZE = 32 * 1024 * 1024; // 32MB

const VideoUploader = ({
  label = "",
  name,
  value,
  onChange,
  videoWidth = 200,
  videoHeight = 200,
  maxFileNum = 1,
  errors,
  disabled = false,
}) => {
  const [files, setFiles] = useState(Array.isArray(value) ? value : []);
  const [errorsMes, setErrorsMes] = useState("");
  const [rejectedFiles, setRejectedFiles] = useState([]);

  useEffect(() => {
    if (value && JSON.stringify(value) !== JSON.stringify(files)) {
      setFiles(Array.isArray(value) ? value : [value]);
    }
  }, [value]);

  const renderVideoPreview = (file) => {
    if (typeof file === "string" && file.includes("http")) {
      return (
        <video style={{ width: videoWidth, height: videoHeight }} controls>
          <source src={file} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      );
    } else if (file && file.type && file.type.startsWith("video")) {
      return (
        <video style={{ width: videoWidth, height: videoHeight }} controls>
          <source src={URL.createObjectURL(file)} type={file.type} />
          Your browser does not support the video tag.
        </video>
      );
    } else {
      return <span>No video selected</span>;
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles, fileRejections) => {
      if (disabled) return;

      const filteredFiles = acceptedFiles.filter((file) =>
        file.type.startsWith("video")
      );

      const newFiles = [...files, ...filteredFiles].slice(0, maxFileNum);
      setRejectedFiles(fileRejections);

      const errorMsgs = fileRejections.map((fileRejection) =>
        fileRejection.errors.map((err) => err.message).join(", ")
      );

      setErrorsMes(errorMsgs.join(", "));
      setFiles(newFiles);
      onChange(newFiles);
    },
    maxSize: MAX_FILE_SIZE,
    accept: {
      "video/*": [],
    },
    disabled: disabled,
  });

  const handleRemoveFile = (index) => {
    if (disabled) return;

    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
    onChange(newFiles);
  };

  const fileList = files.map((file, index) => (
    <div key={index} style={{ display: "flex", gap: "2rem" }}>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div className="file-preview">{renderVideoPreview(file)}</div>
        {!disabled && (
          <Button
            variant="outlined"
            color="error"
            onClick={() => handleRemoveFile(index)}
            sx={{ width: "100%" }}
          >
            Remove video
          </Button>
        )}
      </div>
    </div>
  ));

  return (
    <Fragment>
      <Box
        sx={{
          //   border: "2px dashed #481AA3",
          borderRadius: "15px",
          width: "30vw",
          minHeight: "30vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#EBE4FA",
          position: "relative",
        }}
      >
        {files.length ? (
          <Fragment>
            <Box sx={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
              {fileList}
            </Box>
          </Fragment>
        ) : null}
        {files.length < maxFileNum && (
          <div
            {...getRootProps({ className: "dropzone" })}
            style={{
              alignItems: "center",
              display: "flex",
              flexDirection: "column",
              cursor: "pointer",
              width: "100%",
              height: "100%",
              justifyContent: "center",
            }}
          >
            <input {...getInputProps()} />
            {!files.length && (
              <Box
                sx={{
                  display: "flex",
                  textAlign: "center",
                  alignItems: "center",
                  flexDirection: "column",
                }}
              >
                <FaVideo
                  style={{ color: "#481AA3", width: "30px", height: "28px" }}
                />
                <Box>
                  <Stack direction={"row"}>
                    <Typography className="dz-message-text">
                      Any video format, max. 32 MB
                    </Typography>
                  </Stack>
                  <Box
                    className="dz-message-btn"
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <Typography>or</Typography>
                  </Box>
                  <Button
                    sx={{
                      background: "White",
                      border: "none",
                      color: "#481AA3",
                      textDecoration: "none",
                      cursor: "pointer",
                      fontSize: "16px",
                      fontWeight: "bold",
                      height: "20px",
                      paddingX: 3,
                      paddingY: 2.5,
                    }}
                  >
                    Upload Video
                  </Button>
                </Box>
              </Box>
            )}
          </div>
        )}
      </Box>

      {(rejectedFiles.length > 0 || errorsMes) && (
        <Typography variant="caption" color="error">
          {errorsMes || "File is larger than 32MB"}
        </Typography>
      )}

      {errors && errors[name] && (
        <Typography variant="caption" color="error">
          {errors[name].message}
        </Typography>
      )}
    </Fragment>
  );
};

export default VideoUploader;
